import {Injectable, OnDestroy} from "@angular/core";
import {Platform} from "ionic-angular";
import {SessionModel} from "./session.model";
import {AnalyticsService, LandingPage} from "./analytics.service";
import {ResponsiveService} from "./responsive.service";
import {NotificationService} from "../notification/notification.service";
import {NavigationService} from "./navigation.service";
import {OrderingContext, OrderingContextIntention} from "../ordering-context/ordering-context";
import {LoadingStatus} from "../common-types/loading-status";
import {OrderingContextModel} from "../ordering-context/ordering-context.model";
import {Subscription} from "rxjs";
import {OrderEvents} from "../order/order-events";
import {RecentOrderService} from "../order/recent-order.service";
import {OrderType} from "../order/order";

@Injectable()
export class SessionController implements OnDestroy {
    private subscriptions: Subscription = new Subscription();

    constructor(private analyticsService: AnalyticsService,
                private platform: Platform,
                private sessionModel: SessionModel,
                private orderingContextModel: OrderingContextModel,
                private recentOrderService: RecentOrderService,
                private responsiveService: ResponsiveService,
                private notificationService: NotificationService,
                private navigationService: NavigationService) {
        console.log("--- SessionController");

        this.subscriptions.add(OrderEvents.orderComplete$.subscribe(() => this.reset()));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    public async goToEntryPage(suppressReset: boolean = false): Promise<void> {
        return this.navigationService.createPage("woa-entry-page", {suppressReset: suppressReset}, undefined, true);
    }

    public async goToLandingPage(suppressReset: boolean = false, setRoot: boolean = false): Promise<void> {
        if (!suppressReset) {
            this.sessionModel.reset();
        }

        if (this.orderingContextModel.menuId) {
            await this.analyticsService.trackLandingPage(LandingPage.ORDERING);
            return this.navigationService.createPage("woa-ordering-page", undefined, undefined, true);
        }

        if (!this.orderingContextModel.stores && !await this.orderingContextModel.loadStoresForSelection(new OrderingContext())) {
            return this.analyticsService.trackLandingPage(LandingPage.LOGIN);
        }

        if (this.orderingContextModel.singleStore || this.orderingContextModel.currentStore) {
            await this.analyticsService.trackLandingPage(LandingPage.ORDERING);
            return this.navigationService.createPage("woa-ordering-page", undefined, undefined, true);
        }

        if (setRoot) {
            await this.analyticsService.trackLandingPage(LandingPage.LOGIN);
            return this.goToEntryPage(suppressReset);
        }

        await this.analyticsService.trackLandingPage(LandingPage.STORE_SELECTION);
        this.sessionModel.notifyInitialLoadStatus(LoadingStatus.LOADED);
        await this.orderingContextModel.selectAndApplyStoreAndOrderType(new OrderingContext()
            .withIntention(OrderingContextIntention.SELECT_STORE)
            .withSelectedTable(this.orderingContextModel.preselectedTableSelection));
        if (this.orderingContextModel.currentStore) {
            return this.navigationService.createPage("woa-ordering-page", undefined, undefined, true);
        }
    }

    /**
     * For a child app, return to the parent app.
     * For a table order, return to the ordering page to encourage further ordering.
     * Otherwise, return to the normal landing page.
     */
    private async reset(): Promise<void> {
        if (this.orderingContextModel.parentAppURL) {
            return this.orderingContextModel.routeToParentApp();
        }

        if (this.orderingContextModel.orderType === OrderType.ORDER_AT_TABLE) {
            if (!this.orderingContextModel.hasStoreGroup) {
                this.sessionModel.reset(true, true);
                return this.navigationService.createPage("woa-ordering-page", undefined, undefined, true);
            }

            if (this.sessionModel.currentApp.defaultMenuId) {
                this.sessionModel.reset(false, true);
                return this.navigationService.createPage("woa-ordering-page", undefined, undefined, true);
            }

            this.sessionModel.reset(false, true);
            return this.goToEntryPage(true);
        }

        return this.goToLandingPage(false, true);
    }

    public async confirmExitApp(): Promise<void> {
        const confirm: boolean = await this.notificationService.displayConfirmation("woa.heading.exit-app", undefined, "woa.message.exit-app",
            "woa.button.common.noThanks", "woa.button.common.ok", true);
        if (confirm) {
            if (this.sessionModel.showExitButton) {
                parent.postMessage("exit-app", "*");
            } else if (this.responsiveService.isApp) {
                this.platform.exitApp();
            } else {
                this.navigationService.exitApp();
            }
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/orchestration/session.controller.ts